import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { viewingChipsQuery } from './ViewingChips.graphql';
import ChipList from './ChipList';
import { gqlTypes } from '../../types/gqlTypes';
import StatusTypeMap from './StatusTypeMap';
import Pagination from './Pagination';
import styles from './ViewingChips.module.scss';

export interface ViewingChipsProps {
  statusType: gqlTypes.ChipStatusType;
}

const PAGE_SIZE = 20;
const OFFSET = 0;

export default function ViewingChips({ statusType }: ViewingChipsProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const queryVariables = {
    limit: PAGE_SIZE,
    offset: OFFSET,
    statusType,
  };
  const { data, loading, fetchMore } = useQuery<gqlTypes.viewingChips, gqlTypes.viewingChipsVariables>(
    viewingChipsQuery,
    {
      variables: queryVariables,
      fetchPolicy: 'cache-and-network',
    },
  );

  const refetchQueries = [{ query: viewingChipsQuery, variables: queryVariables }];

  const chipReseller = data?.currentUser.chipReseller;
  const chips = chipReseller?.chips.chips ?? [];
  const metadata = chipReseller?.chips.metadata;
  const resellerName = chipReseller?.name;

  const limit = metadata?.limit ?? PAGE_SIZE;
  const offset = metadata?.offset ?? OFFSET;
  const totalChipCount = metadata?.totalChipCount ?? 0;

  const totalPages = Math.ceil(totalChipCount / limit);

  useEffect(() => {
    if (metadata) {
      const calculatedPage = offset ? offset / limit + 1 : 1;
      setCurrentPage(calculatedPage);
    }
  }, [metadata, offset, limit]);

  const handlePaginationClick = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    fetchMore({
      variables: {
        limit: PAGE_SIZE,
        offset: pageNumber > 1 ? (pageNumber - 1) * limit : 0,
        statusType,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          currentUser: {
            ...prev.currentUser,
            ...fetchMoreResult.currentUser,
          },
        };
      },
    });
  };

  const isStockView = statusType === gqlTypes.ChipStatusType.SHIPPED;
  const isRegisteredView = statusType === gqlTypes.ChipStatusType.ACTIVE;
  const showDateOwner = isRegisteredView;

  return loading && chips.length === 0 ? (
    <div>Loading...</div>
  ) : (
    <>
      {isStockView && resellerName && (
        <p className={styles.stockNote}>
          <span className={styles.stockNoteLabel}>NOTE:</span> These stock chips are already pre-registered to{' '}
          {resellerName} and do not need to be manually registered to your organization.
        </p>
      )}
      <ChipList
        chips={chips}
        statusTypeMap={StatusTypeMap.uniform(statusType)}
        refetchQueries={refetchQueries}
        showDateOwner={showDateOwner}
      />
      {chips.length > 0 && (
        <Pagination
          currentPage={currentPage}
          handleClick={handlePaginationClick}
          totalPages={totalPages}
          totalResults={totalChipCount}
          currentResultsCount={chips.length}
        />
      )}
    </>
  );
}
